import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Tooltip,
  Paper,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  LinearProgress,
} from '@mui/material';
import {
  Check,
  Close,
  Visibility,
  OpenInNew,
  Refresh,
  PlayArrow,
  Edit,
  Delete,
} from '@mui/icons-material';
import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
  Timestamp,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../services/firebase';

// ── Types ────────────────────────────────────────────────────────────────────

interface ScrapedOffer {
  id: string;
  venueId: string;
  venueName: string;
  venueWebsite: string;
  venueAddress: string;
  title: string;
  description: string;
  offerType: string;
  daysOfWeek: string[];
  timeStart: string | null;
  timeEnd: string | null;
  price: string | null;
  confidence: number;
  sourceUrl: string;
  notes: string | null;
  status: 'pending_review' | 'approved' | 'rejected';
  scrapedAt: Timestamp;
  expiresAt: Timestamp;
}

const OFFER_TYPE_LABELS: Record<string, string> = {
  happy_hour: 'Happy Hour',
  event: 'Event',
  promotion: 'Promotion',
  special: 'Special',
  food_special: 'Food Special',
  drink_special: 'Drink Special',
};

const OFFER_TYPE_COLORS: Record<string, 'warning' | 'info' | 'success' | 'default' | 'error' | 'primary' | 'secondary'> = {
  happy_hour: 'warning',
  event: 'info',
  promotion: 'success',
  special: 'primary',
  food_special: 'default',
  drink_special: 'secondary',
};

const DAY_SHORT: Record<string, string> = {
  monday: 'Mon', tuesday: 'Tue', wednesday: 'Wed', thursday: 'Thu',
  friday: 'Fri', saturday: 'Sat', sunday: 'Sun',
};

// ── Confidence badge ─────────────────────────────────────────────────────────
const ConfidenceBadge: React.FC<{ value: number }> = ({ value }) => {
  const pct = Math.round(value * 100);
  const color = pct >= 80 ? '#2e7d32' : pct >= 60 ? '#e65100' : '#c62828';
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <Box sx={{ width: 40, mr: 0.5 }}>
        <LinearProgress
          variant="determinate"
          value={pct}
          sx={{
            height: 6,
            borderRadius: 3,
            bgcolor: '#f0f0f0',
            '& .MuiLinearProgress-bar': { bgcolor: color, borderRadius: 3 },
          }}
        />
      </Box>
      <Typography variant="caption" sx={{ color, fontWeight: 600 }}>
        {pct}%
      </Typography>
    </Box>
  );
};

// ── Main Component ────────────────────────────────────────────────────────────
const ScrapedOffersReview: React.FC = () => {
  const [offers, setOffers] = useState<ScrapedOffer[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string>('pending_review');
  const [viewOffer, setViewOffer] = useState<ScrapedOffer | null>(null);
  const [editOffer, setEditOffer] = useState<ScrapedOffer | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [scraperRunning, setScraperRunning] = useState(false);
  const [scraperResult, setScraperResult] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<ScrapedOffer | null>(null);
  const [stateFilter, setStateFilter] = useState<string>('all');
  const [suburbFilter, setSuburbFilter] = useState<string>('all');

  // Edit form state
  const [editTitle, setEditTitle] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editOfferType, setEditOfferType] = useState('');
  const [editTimeStart, setEditTimeStart] = useState('');
  const [editTimeEnd, setEditTimeEnd] = useState('');
  const [editPrice, setEditPrice] = useState('');

  // ── Real-time listener ──────────────────────────────────────────────────────
  useEffect(() => {
    setLoading(true);
    const q = statusFilter === 'all'
      ? query(collection(db, 'scraped_offers'), orderBy('scrapedAt', 'desc'))
      : query(
          collection(db, 'scraped_offers'),
          where('status', '==', statusFilter),
          orderBy('scrapedAt', 'desc')
        );

    const unsub = onSnapshot(q, (snap) => {
      setOffers(snap.docs.map(d => ({ id: d.id, ...d.data() } as ScrapedOffer)));
      setLoading(false);
    }, (err) => {
      console.error('Snapshot error:', err);
      setError('Failed to load scraped offers.');
      setLoading(false);
    });

    return () => unsub();
  }, [statusFilter]);

  // ── Review action ────────────────────────────────────────────────────────────
  const handleReview = useCallback(async (
    offerId: string,
    action: 'approve' | 'reject',
    edits?: object
  ) => {
    setActionLoading(offerId);
    setError(null);
    try {
      const reviewFn = httpsCallable(functions, 'reviewScrapedOffer');
      await reviewFn({ offerId, action, edits });
      setViewOffer(null);
      setEditOffer(null);
    } catch (err: any) {
      setError(err.message || 'Action failed. Please try again.');
    } finally {
      setActionLoading(null);
    }
  }, []);

  // ── Delete approved/rejected offer ──────────────────────────────────────────
  const handleDelete = useCallback(async (offer: ScrapedOffer) => {
    setActionLoading(offer.id);
    setError(null);
    try {
      const deleteFn = httpsCallable(functions, 'deleteScrapedOffer');
      await deleteFn({ offerId: offer.id });
      setDeleteConfirm(null);
    } catch (err: any) {
      setError(err.message || 'Delete failed. Please try again.');
    } finally {
      setActionLoading(null);
    }
  }, []);

  // ── Open edit dialog ────────────────────────────────────────────────────────
  const openEditDialog = (offer: ScrapedOffer) => {
    setEditOffer(offer);
    setEditTitle(offer.title);
    setEditDescription(offer.description);
    setEditOfferType(offer.offerType);
    setEditTimeStart(offer.timeStart || '');
    setEditTimeEnd(offer.timeEnd || '');
    setEditPrice(offer.price || '');
  };

  const handleApproveWithEdits = () => {
    if (!editOffer) return;
    handleReview(editOffer.id, 'approve', {
      title: editTitle,
      description: editDescription,
      offerType: editOfferType,
      timeStart: editTimeStart || null,
      timeEnd: editTimeEnd || null,
      price: editPrice || null,
    });
  };

  // ── Manual scraper trigger ─────────────────────────────────────────────────
  const handleRunScraper = async () => {
    setScraperRunning(true);
    setScraperResult(null);
    setError(null);
    try {
      // Set a generous timeout — the function returns immediately but Firebase
      // SDK default (70s) can still be hit on slow cold starts
      const triggerFn = httpsCallable(functions, 'triggerVenueScraper', { timeout: 120000 });
      const result: any = await triggerFn({});
      const d = result.data;
      setScraperResult(
        d.message || `Scraper started for ${d.venuesQueued} venues. New offers will appear in this tab as they are found.`
      );
    } catch (err: any) {
      setError(err.message || 'Scraper failed to run.');
    } finally {
      setScraperRunning(false);
    }
  };

  // ── Stats ────────────────────────────────────────────────────────────────────
  const pendingCount = offers.filter(o => o.status === 'pending_review').length;

  // Extract state from address — matches 'NSW', 'VIC', 'QLD' etc
  const parseState = (address: string) =>
    address?.match(/\b(NSW|VIC|QLD|SA|WA|TAS|NT|ACT)\b/)?.[1] || '';

  // Extract suburb — word(s) before the state abbreviation
  const parseSuburb = (address: string) => {
    const m = address?.match(/([A-Za-z ]+?)\s+(?:NSW|VIC|QLD|SA|WA|TAS|NT|ACT)/);
    return m?.[1]?.trim() || '';
  };

  const availableStates = ['all', ...Array.from(new Set(
    offers.map(o => parseState(o.venueAddress)).filter(Boolean)
  )).sort()];

  const availableSuburbs = ['all', ...Array.from(new Set(
    offers
      .filter(o => stateFilter === 'all' || parseState(o.venueAddress) === stateFilter)
      .map(o => parseSuburb(o.venueAddress))
      .filter(Boolean)
  )).sort()];

  const filteredOffers = offers.filter(o => {
    if (stateFilter !== 'all' && parseState(o.venueAddress) !== stateFilter) return false;
    if (suburbFilter !== 'all' && parseSuburb(o.venueAddress) !== suburbFilter) return false;
    return true;
  });

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={700}>
            Scraped Offers Review
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Review AI-extracted offers from venue websites before they go live in the app.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            startIcon={scraperRunning ? <CircularProgress size={16} /> : <PlayArrow />}
            onClick={handleRunScraper}
            disabled={scraperRunning}
            sx={{ borderColor: '#F24F2F', color: '#F24F2F', '&:hover': { borderColor: '#d63d1e', bgcolor: '#fff5f3' } }}
          >
            {scraperRunning ? 'Running...' : 'Run Scraper Now'}
          </Button>
        </Box>
      </Box>

      {/* Alerts */}
      {scraperResult && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setScraperResult(null)}>
          {scraperResult}
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Status filter tabs */}
      <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
        {[
          { value: 'pending_review', label: `Pending Review${pendingCount > 0 ? ` (${pendingCount})` : ''}` },
          { value: 'approved', label: 'Approved' },
          { value: 'rejected', label: 'Rejected' },
          { value: 'all', label: 'All' },
        ].map(opt => (
          <Chip
            key={opt.value}
            label={opt.label}
            onClick={() => setStatusFilter(opt.value)}
            color={statusFilter === opt.value ? 'primary' : 'default'}
            variant={statusFilter === opt.value ? 'filled' : 'outlined'}
            sx={statusFilter === opt.value ? { bgcolor: '#F24F2F', '&:hover': { bgcolor: '#d63d1e' } } : {}}
          />
        ))}
      </Box>

      {/* State + Suburb filters */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>State</InputLabel>
          <Select
            value={stateFilter}
            label="State"
            onChange={(e) => { setStateFilter(e.target.value); setSuburbFilter('all'); }}
          >
            {availableStates.map(s => (
              <MenuItem key={s} value={s}>{s === 'all' ? 'All States' : s}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Suburb</InputLabel>
          <Select
            value={suburbFilter}
            label="Suburb"
            onChange={(e) => setSuburbFilter(e.target.value)}
          >
            {availableSuburbs.map(s => (
              <MenuItem key={s} value={s}>{s === 'all' ? 'All Suburbs' : s}</MenuItem>
            ))}
          </Select>
        </FormControl>
        {(stateFilter !== 'all' || suburbFilter !== 'all') && (
          <Chip
            label={`${filteredOffers.length} results`}
            size="small"
            onDelete={() => { setStateFilter('all'); setSuburbFilter('all'); }}
            sx={{ alignSelf: 'center' }}
          />
        )}
      </Box>

      {/* Table */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress sx={{ color: '#F24F2F' }} />
        </Box>
      ) : offers.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h6" color="text.secondary">No offers in this category</Typography>
          <Typography variant="body2" color="text.disabled" sx={{ mt: 1 }}>
            {statusFilter === 'pending_review'
              ? 'Run the scraper to fetch new offers from venue websites.'
              : 'Nothing here yet.'}
          </Typography>
        </Box>
      ) : (
        <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: '#fafafa' }}>
                <TableCell sx={{ fontWeight: 700 }}>Venue</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Offer</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Days / Time</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Confidence</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Scraped</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredOffers.map(offer => (
                <TableRow key={offer.id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>{offer.venueName}</Typography>
                    <Typography variant="caption" color="text.secondary">{offer.venueAddress}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>{offer.title}</Typography>
                    {offer.price && (
                      <Typography variant="caption" color="text.secondary">{offer.price}</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={OFFER_TYPE_LABELS[offer.offerType] || offer.offerType}
                      color={OFFER_TYPE_COLORS[offer.offerType] || 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption">
                      {offer.daysOfWeek?.length > 0
                        ? offer.daysOfWeek.map(d => DAY_SHORT[d] || d).join(', ')
                        : '—'}
                    </Typography>
                    {(offer.timeStart || offer.timeEnd) && (
                      <Typography variant="caption" display="block" color="text.secondary">
                        {offer.timeStart || '?'} – {offer.timeEnd || '?'}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <ConfidenceBadge value={offer.confidence} />
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption">
                      {offer.scrapedAt?.toDate?.()?.toLocaleDateString('en-AU', {
                        day: '2-digit', month: 'short',
                      })}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={offer.status === 'pending_review' ? 'Pending' : offer.status === 'approved' ? 'Approved' : 'Rejected'}
                      color={offer.status === 'approved' ? 'success' : offer.status === 'rejected' ? 'error' : 'warning'}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                      <Tooltip title="View details">
                        <IconButton size="small" onClick={() => setViewOffer(offer)}>
                          <Visibility fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Source page">
                        <IconButton size="small" onClick={() => window.open(offer.sourceUrl, '_blank')}>
                          <OpenInNew fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {offer.status === 'pending_review' && (
                        <>
                          <Tooltip title="Edit & Approve">
                            <IconButton size="small" onClick={() => openEditDialog(offer)} sx={{ color: '#1976d2' }}>
                              <Edit fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Approve">
                            <IconButton
                              size="small"
                              onClick={() => handleReview(offer.id, 'approve')}
                              disabled={actionLoading === offer.id}
                              sx={{ color: '#2e7d32' }}
                            >
                              {actionLoading === offer.id
                                ? <CircularProgress size={16} />
                                : <Check fontSize="small" />}
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Reject">
                            <IconButton
                              size="small"
                              onClick={() => handleReview(offer.id, 'reject')}
                              disabled={actionLoading === offer.id}
                              sx={{ color: '#c62828' }}
                            >
                              <Close fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                      {(offer.status === 'approved' || offer.status === 'rejected') && (
                        <Tooltip title="Delete permanently">
                          <IconButton
                            size="small"
                            onClick={() => setDeleteConfirm(offer)}
                            disabled={actionLoading === offer.id}
                            sx={{ color: '#c62828' }}
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* ── View Details Dialog ─────────────────────────────────────────────── */}
      <Dialog open={!!viewOffer} onClose={() => setViewOffer(null)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700 }}>{viewOffer?.title}</DialogTitle>
        <DialogContent>
          {viewOffer && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">Venue</Typography>
                <Typography>{viewOffer.venueName}</Typography>
                <Typography variant="caption" color="text.secondary">{viewOffer.venueAddress}</Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">Description</Typography>
                <Typography variant="body2">{viewOffer.description || '—'}</Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">Type</Typography>
                  <Chip label={OFFER_TYPE_LABELS[viewOffer.offerType] || viewOffer.offerType} size="small" color={OFFER_TYPE_COLORS[viewOffer.offerType] || 'default'} />
                </Box>
                {viewOffer.price && (
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">Price</Typography>
                    <Typography variant="body2">{viewOffer.price}</Typography>
                  </Box>
                )}
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">Confidence</Typography>
                  <ConfidenceBadge value={viewOffer.confidence} />
                </Box>
              </Box>
              {viewOffer.daysOfWeek?.length > 0 && (
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">Days</Typography>
                  <Typography variant="body2">{viewOffer.daysOfWeek.map(d => DAY_SHORT[d] || d).join(', ')}</Typography>
                </Box>
              )}
              {(viewOffer.timeStart || viewOffer.timeEnd) && (
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">Time</Typography>
                  <Typography variant="body2">{viewOffer.timeStart || '?'} – {viewOffer.timeEnd || '?'}</Typography>
                </Box>
              )}
              {viewOffer.notes && (
                <Alert severity="info" sx={{ fontSize: '0.8rem' }}>{viewOffer.notes}</Alert>
              )}
              <Box>
                <Typography variant="subtitle2" color="text.secondary">Source</Typography>
                <Button size="small" startIcon={<OpenInNew />} onClick={() => window.open(viewOffer.sourceUrl, '_blank')} sx={{ p: 0, color: '#F24F2F' }}>
                  {viewOffer.sourceUrl}
                </Button>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewOffer(null)}>Close</Button>
          {viewOffer?.status === 'pending_review' && (
            <>
              <Button
                onClick={() => { setViewOffer(null); openEditDialog(viewOffer!); }}
                startIcon={<Edit />}
              >
                Edit & Approve
              </Button>
              <Button
                onClick={() => handleReview(viewOffer!.id, 'reject')}
                color="error"
                disabled={actionLoading === viewOffer?.id}
              >
                Reject
              </Button>
              <Button
                onClick={() => handleReview(viewOffer!.id, 'approve')}
                variant="contained"
                color="success"
                disabled={actionLoading === viewOffer?.id}
                startIcon={actionLoading === viewOffer?.id ? <CircularProgress size={16} color="inherit" /> : <Check />}
              >
                Approve
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* ── Edit & Approve Dialog ──────────────────────────────────────────── */}
      <Dialog open={!!editOffer} onClose={() => setEditOffer(null)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700 }}>Edit & Approve Offer</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Title"
              value={editTitle}
              onChange={e => setEditTitle(e.target.value)}
              fullWidth
              size="small"
            />
            <TextField
              label="Description"
              value={editDescription}
              onChange={e => setEditDescription(e.target.value)}
              fullWidth
              multiline
              rows={3}
              size="small"
            />
            <FormControl size="small" fullWidth>
              <InputLabel>Offer Type</InputLabel>
              <Select value={editOfferType} onChange={e => setEditOfferType(e.target.value)} label="Offer Type">
                {Object.entries(OFFER_TYPE_LABELS).map(([val, label]) => (
                  <MenuItem key={val} value={val}>{label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                label="Start Time"
                placeholder="18:00"
                value={editTimeStart}
                onChange={e => setEditTimeStart(e.target.value)}
                size="small"
                sx={{ flex: 1 }}
              />
              <TextField
                label="End Time"
                placeholder="20:00"
                value={editTimeEnd}
                onChange={e => setEditTimeEnd(e.target.value)}
                size="small"
                sx={{ flex: 1 }}
              />
            </Box>
            <TextField
              label="Price (optional)"
              placeholder="e.g. $10 pints"
              value={editPrice}
              onChange={e => setEditPrice(e.target.value)}
              size="small"
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOffer(null)}>Cancel</Button>
          <Button
            onClick={handleApproveWithEdits}
            variant="contained"
            color="success"
            disabled={!editTitle.trim() || actionLoading === editOffer?.id}
            startIcon={actionLoading === editOffer?.id ? <CircularProgress size={16} color="inherit" /> : <Check />}
            sx={{ bgcolor: '#2e7d32' }}
          >
            Approve with Edits
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Delete Confirmation Dialog ─────────────────────────────────────── */}
      <Dialog open={!!deleteConfirm} onClose={() => setDeleteConfirm(null)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700 }}>Delete Offer</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            This will permanently remove <strong>{deleteConfirm?.title}</strong> from both the review queue and the live offers in the app. This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm(null)}>Cancel</Button>
          <Button
            onClick={() => deleteConfirm && handleDelete(deleteConfirm)}
            color="error"
            variant="contained"
            disabled={actionLoading === deleteConfirm?.id}
            startIcon={actionLoading === deleteConfirm?.id ? <CircularProgress size={16} color="inherit" /> : <Delete />}
          >
            Delete Permanently
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ScrapedOffersReview;